/**
 * User-scoped Business notification inbox.
 *
 * The route intentionally sits outside the organization and facility gates, so
 * operators can read account notifications while setup is pending or before a
 * facility is selected. The backend owns audience filtering and read state; this
 * component only renders that server read model with native MUI components.
 */
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import DoneAllRoundedIcon from "@mui/icons-material/DoneAllRounded";
import NotificationsNoneRoundedIcon from "@mui/icons-material/NotificationsNoneRounded";
import Alert from "@mui/material/Alert";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemText from "@mui/material/ListItemText";
import Link from "@mui/material/Link";
import Pagination from "@mui/material/Pagination";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { notificationApi } from "@bw/shared/api";
import type { NotificationResponse } from "@bw/shared/types/generated";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { sanitizeNext } from "@bw/shared";

const PAGE_SIZE = 20;

function formatRelativeTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  const seconds = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (seconds < 60) return "Just now";
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function NotificationIcon({ unread }: { unread: boolean }) {
  return (
    <ListItemAvatar>
      <Badge color="primary" invisible={!unread} variant="dot">
        <Box
          sx={{
            alignItems: "center",
            bgcolor: "action.hover",
            borderRadius: "50%",
            display: "flex",
            height: 36,
            justifyContent: "center",
            width: 36,
          }}
        >
          <NotificationsNoneRoundedIcon fontSize="small" aria-hidden="true" />
        </Box>
      </Badge>
    </ListItemAvatar>
  );
}

function NotificationRow({
  notification,
  markRead,
  markingRead,
}: {
  notification: NotificationResponse;
  markRead: (notificationId: string) => void;
  markingRead: boolean;
}) {
  const unread = !notification.is_read;
  const action = sanitizeNext(notification.action_url);

  return (
    <ListItem
      divider
      secondaryAction={
        unread ? (
          <IconButton
            aria-label="Mark as read"
            disabled={markingRead}
            onClick={() => markRead(notification.notification_id)}
            size="small"
            title="Mark as read"
            type="button"
          >
            <CheckRoundedIcon fontSize="small" aria-hidden="true" />
          </IconButton>
        ) : null
      }
      sx={{
        bgcolor: unread ? "action.hover" : "background.paper",
        py: 0.5,
        pr: unread ? 7 : 2,
      }}
    >
      <NotificationIcon unread={unread} />
      <ListItemText
        primary={
          action ? (
            <Link component={RouterLink} to={action} color="inherit">
              {notification.title}
            </Link>
          ) : (
            notification.title
          )
        }
        secondary={
          <Stack component="span" spacing={0.25} sx={{ mt: 0.25 }}>
            {notification.body ? (
              <Typography
                color="text.secondary"
                component="span"
                variant="body2"
              >
                {notification.body}
              </Typography>
            ) : null}
            <Typography
              color="text.secondary"
              component="span"
              variant="caption"
            >
              {formatRelativeTime(notification.created_at)}
            </Typography>
          </Stack>
        }
        slotProps={{
          primary: { sx: { fontWeight: unread ? 700 : 500 } },
        }}
      />
    </ListItem>
  );
}

function LoadingInbox() {
  return (
    <Box aria-label="Loading notifications" role="status">
      {Array.from({ length: 3 }, (_, index) => (
        <Stack
          direction="row"
          key={index}
          spacing={1.5}
          sx={{
            alignItems: "center",
            borderBottom: 1,
            borderColor: "divider",
            px: 2,
            py: 1.5,
          }}
        >
          <Skeleton height={36} variant="circular" width={36} />
          <Stack sx={{ flex: 1 }}>
            <Skeleton width="68%" />
            <Skeleton width="42%" />
          </Stack>
        </Stack>
      ))}
    </Box>
  );
}

export function NotificationsPage() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const query = useQuery({
    queryKey: [
      "notifications",
      "list",
      { page, pageSize: PAGE_SIZE, audience: "business" },
    ],
    queryFn: () =>
      notificationApi.list({
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
        audience: "business",
      }),
  });
  const markRead = useMutation({
    mutationFn: (notificationId: string) =>
      notificationApi.markRead(notificationId),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["notifications"] }),
  });
  const markAllRead = useMutation({
    mutationFn: () => notificationApi.markAllRead("business"),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["notifications"] }),
  });

  const items = query.data?.items ?? [];
  const totalPages = Math.ceil((query.data?.total ?? 0) / PAGE_SIZE);
  const hasUnread = items.some((notification) => !notification.is_read);
  const markingRead = markRead.isPending || markAllRead.isPending;

  const handleMarkRead = (notificationId: string) => {
    markAllRead.reset();
    markRead.mutate(notificationId);
  };

  const handleMarkAllRead = () => {
    markRead.reset();
    markAllRead.mutate();
  };

  const handlePageChange = (selectedPage: number) => {
    if (markingRead) return;
    markRead.reset();
    markAllRead.reset();
    setPage(selectedPage);
  };

  return (
    <Box
      sx={{
        mx: "auto",
        pb: 5,
        px: { xs: 2, sm: 3 },
        width: "100%",
        maxWidth: 960,
      }}
    >
      <Paper
        aria-label="Notifications inbox"
        component="section"
        sx={{ overflow: "hidden", width: "100%" }}
        variant="outlined"
      >
        <Stack
          component="header"
          direction="row"
          spacing={2}
          sx={{
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 1.5,
          }}
        >
          <Typography component="h1" variant="h5">
            Notifications
          </Typography>
          {hasUnread ? (
            <Button
              disabled={markingRead}
              onClick={handleMarkAllRead}
              size="small"
              startIcon={<DoneAllRoundedIcon aria-hidden="true" />}
              type="button"
            >
              Mark all as read
            </Button>
          ) : null}
        </Stack>

        {markRead.isError ? (
          <Alert role="alert" severity="error" sx={{ mx: 2, mb: 1 }}>
            Couldn&apos;t mark this notification as read. Try again.
          </Alert>
        ) : null}

        {markAllRead.isError ? (
          <Alert role="alert" severity="error" sx={{ mx: 2, mb: 1 }}>
            Couldn&apos;t mark all notifications as read. Try again.
          </Alert>
        ) : null}

        {query.isLoading ? <LoadingInbox /> : null}

        {query.isError ? (
          <Alert
            aria-label="Failed to load notifications. Please try again."
            severity="error"
            sx={{ m: 2 }}
          >
            Failed to load notifications. Please try again.
          </Alert>
        ) : null}

        {!query.isLoading && !query.isError && items.length === 0 ? (
          <Stack
            spacing={0.75}
            sx={{ alignItems: "center", px: 2, py: 5, textAlign: "center" }}
          >
            <NotificationsNoneRoundedIcon
              color="action"
              sx={{ fontSize: 40 }}
              aria-hidden="true"
            />
            <Typography component="h2" variant="h6">
              No notifications
            </Typography>
            <Typography color="text.secondary" variant="body2">
              Updates for your account will appear here.
            </Typography>
          </Stack>
        ) : null}

        {!query.isLoading && !query.isError && items.length > 0 ? (
          <>
            <List disablePadding>
              {items.map((notification) => (
                <NotificationRow
                  key={notification.notification_id}
                  markingRead={markingRead}
                  markRead={handleMarkRead}
                  notification={notification}
                />
              ))}
            </List>
            {totalPages > 1 ? (
              <Stack
                sx={{
                  alignItems: "center",
                  borderTop: 1,
                  borderColor: "divider",
                  px: 2,
                  py: 1.5,
                }}
              >
                <Pagination
                  count={totalPages}
                  disabled={markingRead}
                  onChange={(_, selectedPage) => handlePageChange(selectedPage)}
                  page={page}
                  shape="rounded"
                  size="small"
                />
              </Stack>
            ) : null}
          </>
        ) : null}
      </Paper>
    </Box>
  );
}
